// 1. React imports
import { useState, useMemo } from 'react'

// 2. External library imports
import { Drawer, Box, Typography, IconButton, Chip, Tabs, Tab, Tooltip } from '@mui/material'
import { Close, ContentCopy, ErrorOutline, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'
import dayjs from 'dayjs'

// 3. Internal imports
import { SentryIssue } from '@/types/sentryIssue.type'
import { TrendGraph } from '@/components/shared/TrendGraph'
import { useIssueDetails } from '@/hooks/react-query/useIssueDetails'
import Loading from '@/components/shared/layout/Loading'
import { DaylightDateFormat } from '@/constants/DaylightDateFormat'

interface IssueDetailPanelProps {
  open: boolean
  onClose: () => void
  issue: SentryIssue | null
  workstream?: string | null
}

export const IssueDetailPanel = ({ open, onClose, issue, workstream }: IssueDetailPanelProps) => {
  const [activeTab, setActiveTab] = useState(0)
  const [isTechnicalDetailsOpen, setIsTechnicalDetailsOpen] = useState(true)
  const [copiedStackTrace, setCopiedStackTrace] = useState(false)
  const [copiedTraceId, setCopiedTraceId] = useState(false)

  // Fetch issue details from API with workstream context (for Apex Client)
  const { data: issueDetailsData, isLoading: isLoadingDetails } = useIssueDetails(issue?.id || null, workstream)

  // Activity chart data from API trend - formatted for TrendGraph
  const activityData = useMemo(() => {
    // Check if we have valid trend data
    const hasTrendData =
      issueDetailsData?.trend?.data &&
      Array.isArray(issueDetailsData.trend.data) &&
      issueDetailsData.trend.data.length > 0

    if (hasTrendData) {
      return issueDetailsData.trend.data.map((item) => ({
        date: typeof item.date === 'string' ? item.date : item.date.toString(),
        count: item.count[0]?.count || 0,
      }))
    }

    // Fallback data
    return [
      {
        date: dayjs().subtract(6, 'days').format(DaylightDateFormat.ISO_DATE),
        count: 0,
      },
      {
        date: dayjs().subtract(5, 'days').format(DaylightDateFormat.ISO_DATE),
        count: 0,
      },
      {
        date: dayjs().subtract(4, 'days').format(DaylightDateFormat.ISO_DATE),
        count: 0,
      },
      {
        date: dayjs().subtract(3, 'days').format(DaylightDateFormat.ISO_DATE),
        count: 0,
      },
      {
        date: dayjs().subtract(2, 'days').format(DaylightDateFormat.ISO_DATE),
        count: 0,
      },
      {
        date: dayjs().subtract(1, 'day').format(DaylightDateFormat.ISO_DATE),
        count: 0,
      },
      { date: dayjs().format(DaylightDateFormat.ISO_DATE), count: 0 },
    ]
  }, [issueDetailsData])

  if (!issue) return null

  // Extract API data according to API documentation
  const traceId = issueDetailsData?.latestEvent?.traceId || 'N/A'
  const url = issueDetailsData?.url || 'N/A'

  // Events: Total count since issue began
  const eventCount = issueDetailsData?.issue?.count
    ? typeof issueDetailsData.issue.count === 'string'
      ? parseInt(issueDetailsData.issue.count, 10)
      : issueDetailsData.issue.count
    : issue.totalEvents || 0

  // Users: Total users affected since issue began
  const userCount = issueDetailsData?.issue?.userCount || issue.affectedUsers || 0

  const firstSeen = issueDetailsData?.issue?.firstSeen || issue.lastSeen
  const lastSeen = issueDetailsData?.issue?.lastSeen || issue.lastSeen

  // Technical Details
  const environment = issueDetailsData?.latestEvent?.environment || 'N/A'
  const browser = issueDetailsData?.latestEvent?.browser?.name || 'N/A'
  const browserVersion = issueDetailsData?.latestEvent?.tags?.find((tag) => tag.key === 'browser')?.value || ''
  const platform = issueDetailsData?.latestEvent?.os?.name || 'N/A'
  const platformVersion = issueDetailsData?.latestEvent?.tags?.find((tag) => tag.key === 'os')?.value || ''
  const apiRoute = url || issue.path || issue.transaction || 'N/A'
  const release = issueDetailsData?.latestEvent?.release || 'N/A'

  // Determine if issue is unhandled
  const isUnhandled = issueDetailsData?.issue?.isUnhandled || issue.isUnhandled || false

  // Error Messages based on isUnhandled status
  let summaryErrorMessage = ''
  let stackTraceMessage = ''

  if (isUnhandled) {
    // isUnhandled: true
    // Summary: issue.title, issue.culprit, latestEvent.environment
    const issueTitle = issueDetailsData?.issue?.title || issue.title || ''
    const issueCulprit = issueDetailsData?.issue?.culprit || issue.path || ''
    const eventEnvironment = issueDetailsData?.latestEvent?.environment || environment || ''
    summaryErrorMessage = `${issueTitle}\n${issueCulprit}\n${eventEnvironment}`

    // Stack Trace: latestEvent.exception.type, latestEvent.exception.value, latestEvent.exception.mechanism.type, handled
    const exceptionType = issueDetailsData?.latestEvent?.exception?.type || ''
    const exceptionValue = issueDetailsData?.latestEvent?.exception?.value || ''
    const mechanismType = issueDetailsData?.latestEvent?.exception?.mechanism?.type || ''
    const mechanismHandled =
      issueDetailsData?.latestEvent?.exception?.mechanism?.handled !== undefined
        ? issueDetailsData.latestEvent.exception.mechanism.handled
        : false

    stackTraceMessage = `${exceptionType}\n${exceptionValue}\nMechanism: ${mechanismType}\nHandled: ${mechanismHandled}`

    // Add formatted stack trace if available
    const stackTrace = issueDetailsData?.latestEvent?.stackTrace
    if (stackTrace && stackTrace.length > 0) {
      const formattedTrace = stackTrace
        .map((frame) => {
          const func = frame.function || 'anonymous'
          const file = frame.filename || 'unknown'
          const line = frame.lineNo ? `:${frame.lineNo}` : ''
          const col = frame.colNo ? `:${frame.colNo}` : ''
          return `  at ${func} (${file}${line}${col})`
        })
        .join('\n')
      stackTraceMessage += `\n\n${formattedTrace}`
    }
  } else {
    // isUnhandled: false
    // Summary: Only show latestEvent.message
    summaryErrorMessage = issueDetailsData?.latestEvent?.message || issue.title || 'No error message'

    // Stack Trace: No stack trace for handled errors
    stackTraceMessage = 'No stack trace available for handled errors'
  }

  const assignedTo = issueDetailsData?.issue?.assignedTo?.name || null

  const handleCopyTraceId = () => {
    navigator.clipboard.writeText(traceId)
    setCopiedTraceId(true)
    setTimeout(() => setCopiedTraceId(false), 2000)
  }

  // Priority styling
  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'high':
        return {
          backgroundColor: '#FEF3F2',
          color: '#B81C1C',
          borderColor: '#FECECE',
          icon: <KeyboardArrowUp sx={{ fontSize: 16 }} />,
        }
      case 'medium':
        return {
          backgroundColor: '#FFF9E6',
          color: '#B8860B',
          borderColor: '#FFE082',
          icon: <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>=</Typography>,
        }
      case 'low':
        return {
          backgroundColor: '#E8F5E9',
          color: '#2E7D32',
          borderColor: '#A5D6A7',
          icon: <KeyboardArrowDown sx={{ fontSize: 16 }} />,
        }
      default:
        return {
          backgroundColor: '#F5F5F5',
          color: '#666',
          borderColor: '#E0E0E0',
          icon: <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>=</Typography>,
        }
    }
  }

  const priorityStyles = getPriorityStyles(issue.priority || 'high')

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '90%', md: 600 },
          maxWidth: '100%',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.08)',
          // Hide scrollbar while keeping scroll functionality
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: '1px solid #E0E0E0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 1, pr: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                mb: 1,
              }}
            >
              <ErrorOutline sx={{ fontSize: 20, color: '#0000008A', mt: '6px' }} />
              <Typography
                sx={{
                  color: '#000000',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  fontWeight: 700,
                  fontStyle: 'Bold',
                  fontSize: '20px',
                  leadingTrim: 'NONE',
                  lineHeight: '175%',
                  letterSpacing: '0.15px',
                }}
              >
                {issue.title}
              </Typography>
            </Box>
            <Typography
              sx={{
                color: '#666',
                mb: 1.25,
                fontWeight: 500,
                fontStyle: 'SemiBold',
                fontSize: '16px',
                leadingTrim: 'NONE',
                lineHeight: '143%',
                letterSpacing: '0.17px',
              }}
            >
              {issue.subtitle || issue.path}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap',
              }}
            >
              {issue.isUnhandled && (
                <Chip
                  label="Unhandled"
                  size="small"
                  sx={{
                    backgroundColor: '#0000001F',
                    color: '#000000',
                    fontWeight: 700,
                    '& .MuiChip-label': {
                      px: 1,
                      gap: 10,
                      padding: '8px 12px',
                    },
                    width: 'auto',
                    height: '36px',
                    borderRadius: '16px',
                    fontStyle: 'Bold',
                    fontSize: '14px',
                    leadingTrim: 'none',
                    lineHeight: '143%',
                    letterSpacing: '0.17px',
                  }}
                />
              )}
              <Chip
                label={(issue.priority?.charAt(0).toUpperCase() || '') + (issue.priority?.slice(1) || '') || 'High'}
                icon={
                  priorityStyles.icon || (
                    <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
                      {(issue.priority?.charAt(0).toUpperCase() || '') + (issue.priority?.slice(1) || '') === 'High' ? (
                        <KeyboardArrowUp sx={{ fontSize: 16 }} />
                      ) : (issue.priority?.charAt(0).toUpperCase() || '') + (issue.priority?.slice(1) || '') ===
                        'Medium' ? (
                        <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>=</Typography>
                      ) : (
                        <KeyboardArrowDown sx={{ fontSize: 16 }} />
                      )}
                    </Typography>
                  )
                }
                size="small"
                sx={{
                  height: '36px',
                  fontSize: '14px',
                  backgroundColor: priorityStyles.backgroundColor,
                  color: priorityStyles.color,
                  fontWeight: 700,
                  border: `1px solid ${priorityStyles.borderColor}`,
                  '& .MuiChip-icon': {
                    color: priorityStyles.color,
                    marginLeft: '6px',
                    marginRight: '-4px',
                    fontSize: '24px',
                  },
                  '& .MuiChip-label': {
                    px: 1,
                    gap: 1,
                    paddingTop: '6px',
                    paddingRight: '12px',
                    paddingBottom: '4px',
                    paddingLeft: '4px',
                    borderRadius: '16px',
                    borderWidth: '1px',
                  },
                }}
              />
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: '#666',
              '&:hover': { backgroundColor: '#F5F5F5' },
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2 },
            // Hide scrollbar while keeping scroll functionality
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
          }}
        >
          {/* Stats and Activity Chart Box */}
          <Box
            sx={{
              border: '1px solid #0000001F',
              borderRadius: '8px',
              overflow: 'visible',
            }}
          >
            {/* Loading State */}
            {isLoadingDetails ? (
              <Box
                sx={{
                  p: 4,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Loading size="3rem" />
              </Box>
            ) : (
              <>
                {/* Stats Cards */}
                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(4, 1fr)',
                      },
                      gap: { xs: 1, sm: 0.5 },
                      mb: { xs: 1.5, sm: 2 },
                      width: '100%',
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: '#666',
                          mb: 0.5,
                          fontWeight: 600,
                          fontStyle: 'SemiBold',
                          fontSize: '16px',
                          leadingTrim: 'NONE',
                          lineHeight: '166%',
                          letterSpacing: '0.4px',
                        }}
                      >
                        Events
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#1a1a1a',
                          fontStyle: 'Bold',
                          fontSize: '18px',
                          leadingTrim: 'NONE',
                          lineHeight: '143%',
                          letterSpacing: '0.17px',
                        }}
                      >
                        {eventCount.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: '#666',
                          mb: 0.5,
                          fontWeight: 600,
                          fontStyle: 'SemiBold',
                          fontSize: '16px',
                          leadingTrim: 'NONE',
                          lineHeight: '166%',
                          letterSpacing: '0.4px',
                        }}
                      >
                        Users
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: '#1a1a1a',
                          fontStyle: 'Bold',
                          fontSize: '18px',
                          leadingTrim: 'NONE',
                          lineHeight: '143%',
                          letterSpacing: '0.17px',
                        }}
                      >
                        {userCount.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: '#666',
                          mb: 0.5,
                          fontWeight: 600,
                          fontStyle: 'SemiBold',
                          fontSize: '16px',
                          leadingTrim: 'NONE',
                          lineHeight: '166%',
                          letterSpacing: '0.4px',
                        }}
                      >
                        First Seen
                      </Typography>
                      {firstSeen ? (
                        <>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#1a1a1a',
                              fontStyle: 'SemiBold',
                              fontSize: '16px',
                              leadingTrim: 'NONE',
                              lineHeight: '166%',
                              letterSpacing: '0.4px',
                            }}
                          >
                            {dayjs(firstSeen).format(DaylightDateFormat.DATE)}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#1a1a1a',
                              fontStyle: 'SemiBold',
                              fontSize: '16px',
                              leadingTrim: 'NONE',
                              lineHeight: '166%',
                              letterSpacing: '0.4px',
                            }}
                          >
                            {dayjs(firstSeen).format(DaylightDateFormat.TIME_12)}
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: '#1a1a1a',
                            fontStyle: 'SemiBold',
                            fontSize: '16px',
                            leadingTrim: 'NONE',
                            lineHeight: '166%',
                            letterSpacing: '0.4px',
                          }}
                        >
                          N/A
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          color: '#666',
                          mb: 0.5,
                          fontWeight: 600,
                          fontStyle: 'SemiBold',
                          fontSize: '16px',
                          leadingTrim: 'NONE',
                          lineHeight: '166%',
                          letterSpacing: '0.4px',
                        }}
                      >
                        Last Seen
                      </Typography>
                      {lastSeen ? (
                        <>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#1a1a1a',
                              fontStyle: 'SemiBold',
                              fontSize: '16px',
                              leadingTrim: 'NONE',
                              lineHeight: '166%',
                              letterSpacing: '0.4px',
                            }}
                          >
                            {dayjs(lastSeen).format(DaylightDateFormat.DATE)}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#1a1a1a',
                              fontStyle: 'SemiBold',
                              fontSize: '16px',
                              leadingTrim: 'NONE',
                              lineHeight: '166%',
                              letterSpacing: '0.4px',
                            }}
                          >
                            {dayjs(lastSeen).format(DaylightDateFormat.TIME_12)}
                          </Typography>
                        </>
                      ) : (
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: '#1a1a1a',
                            fontStyle: 'SemiBold',
                            fontSize: '16px',
                            leadingTrim: 'NONE',
                            lineHeight: '166%',
                            letterSpacing: '0.4px',
                          }}
                        >
                          N/A
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Activity Chart */}
                  <Box
                    sx={{
                      borderTop: '1px solid #0000001F',
                      pt: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#00000099',
                        mb: { xs: 1, sm: 1.5 },
                        fontWeight: 600,
                        fontStyle: 'SemiBold',
                        fontSize: { xs: '14px', sm: '16px' },
                        leadingTrim: 'NONE',
                        lineHeight: '166%',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Activity (7 days)
                    </Typography>
                    <Box sx={{ height: { xs: 60, sm: 80 }, width: '100%' }}>
                      <TrendGraph
                        data={activityData}
                        color="#3B82F6"
                        gradientId="activityChartGradient"
                        width={500}
                        height={80}
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {/* Technical Details Box */}
          <Box
            sx={{
              border: '1px solid #0000001F',
              borderRadius: '8px',
              overflow: 'visible',
            }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: isTechnicalDetailsOpen ? 2 : 0,
                  cursor: 'pointer',
                }}
                onClick={() => setIsTechnicalDetailsOpen(!isTechnicalDetailsOpen)}
              >
                <Typography
                  sx={{
                    color: '#0000008A',
                    fontWeight: 700,
                    fontStyle: 'Bold',
                    fontSize: '18px',
                    leadingTrim: 'NONE',
                    lineHeight: '175%',
                    letterSpacing: '0.15px',
                  }}
                >
                  Technical Details
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    color: '#0000008A',
                    '&:hover': { backgroundColor: '#F5F5F5' },
                  }}
                >
                  {isTechnicalDetailsOpen ? (
                    <KeyboardArrowUp sx={{ fontSize: 24, color: '#0000008A' }} />
                  ) : (
                    <KeyboardArrowDown sx={{ fontSize: 24, color: '#0000008A' }} />
                  )}
                </IconButton>
              </Box>

              {isTechnicalDetailsOpen && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#00000099',
                        fontWeight: 500,
                        fontStyle: 'SemiBold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '166%',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Environment
                    </Typography>
                    <Typography
                      sx={{
                        color: '#000000',
                        fontWeight: 700,
                        fontStyle: 'Bold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '143%',
                        letterSpacing: '0.17px',
                      }}
                    >
                      {environment}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#666',
                        fontWeight: 500,
                        fontStyle: 'SemiBold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '166%',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Browser
                    </Typography>
                    <Typography
                      sx={{
                        color: '#000000',
                        fontWeight: 700,
                        fontStyle: 'Bold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '143%',
                        letterSpacing: '0.17px',
                      }}
                    >
                      {browserVersion || browser}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#00000099',
                        fontWeight: 500,
                        fontStyle: 'SemiBold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '166%',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Platform
                    </Typography>
                    <Typography
                      sx={{
                        color: '#000000',
                        fontWeight: 700,
                        fontStyle: 'Bold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '143%',
                        letterSpacing: '0.17px',
                      }}
                    >
                      {platformVersion || platform}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#666',
                        fontWeight: 500,
                        fontStyle: 'SemiBold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '166%',
                        letterSpacing: '0.4px',
                        flexShrink: 0,
                      }}
                    >
                      API Route
                    </Typography>
                    <Tooltip title={apiRoute} placement="top" arrow>
                      <Typography
                        sx={{
                          color: '#000000',
                          fontWeight: 700,
                          fontStyle: 'Bold',
                          fontSize: '18px',
                          leadingTrim: 'NONE',
                          lineHeight: '143%',
                          letterSpacing: '0.17px',
                          textAlign: 'right',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '300px',
                          cursor: 'pointer',
                        }}
                      >
                        {apiRoute}
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#00000099',
                        fontWeight: 500,
                        fontStyle: 'SemiBold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '166%',
                        letterSpacing: '0.4px',
                        flexShrink: 0,
                      }}
                    >
                      Trace ID
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title={traceId} placement="top" arrow>
                        <Typography
                          sx={{
                            color: '#026AB7',
                            fontWeight: 700,
                            fontStyle: 'Bold',
                            fontSize: '18px',
                            leadingTrim: 'NONE',
                            lineHeight: '143%',
                            letterSpacing: '0.17px',
                            fontFamily: 'monospace',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '270px',
                            cursor: 'pointer',
                          }}
                        >
                          {traceId}
                        </Typography>
                      </Tooltip>
                      <Tooltip
                        title="Copied!"
                        open={copiedTraceId}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        placement="top"
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={handleCopyTraceId}
                          sx={{
                            padding: '4px',
                            color: '#3B82F6',
                            '&:hover': { backgroundColor: '#F5F5F5' },
                            flexShrink: 0,
                          }}
                        >
                          <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#00000099',
                        fontWeight: 500,
                        fontStyle: 'SemiBold',
                        fontSize: '18px',
                        leadingTrim: 'NONE',
                        lineHeight: '166%',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Release
                    </Typography>
                    <Tooltip title={release} placement="top" arrow>
                      <Typography
                        sx={{
                          color: '#000000',
                          fontWeight: 700,
                          fontStyle: 'Bold',
                          fontSize: '18px',
                          leadingTrim: 'NONE',
                          lineHeight: '143%',
                          letterSpacing: '0.17px',
                          textAlign: 'right',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '300px',
                          cursor: 'pointer',
                        }}
                      >
                        {release}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Tabs Section */}
          <Box
            sx={{
              border: '1px solid #0000001F',
              borderRadius: '8px',
              overflow: 'visible',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              TabIndicatorProps={{
                style: {
                  left: 0,
                  width: '50%',
                  transform: activeTab === 0 ? 'translateX(0%)' : 'translateX(100%)',
                  transition: 'transform 0.3s ease',
                },
              }}
              sx={{
                minHeight: 48,
                borderBottom: '1px solid #0000001F',
                px: 2,
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'space-around',
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontStyle: 'SemiBold',
                  color: '#00000099',
                  letterSpacing: '0.4px',
                  lineHeight: '166%',
                  '&.Mui-selected': {
                    color: '#026AB7',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#026AB7',
                  height: 3,
                },
              }}
            >
              <Tab label="Summary" />
              <Tab label="Stack Trace" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              {activeTab === 0 && (
                <>
                  {/* Error Message */}
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#D32F2F1F',
                      borderRadius: '8px',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '8px',
                          border: '1px solid #D32F2F',
                          backgroundColor: '#D32F2F1F',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <ErrorOutline sx={{ fontSize: 32, color: '#D32F2F' }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '18px',
                            fontWeight: 600,
                            fontStyle: 'SemiBold',
                            color: '#00000099',
                            letterSpacing: '0.4px',
                            lineHeight: '166%',
                          }}
                        >
                          Error Message
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '18px',
                            fontWeight: 700,
                            fontStyle: 'SemiBold',
                            color: '#00000099',
                            lineHeight: '166%',
                            letterSpacing: '0.4px',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {summaryErrorMessage}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Assigned To */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '18px',
                          color: '#00000099',
                          mb: 0.25,
                          fontWeight: 500,
                          fontStyle: 'SemiBold',
                          letterSpacing: '0.17px',
                          lineHeight: '143%',
                        }}
                      >
                        Assigned To
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: '#E0E0E0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#666',
                            }}
                          >
                            {assignedTo ? assignedTo.charAt(0).toUpperCase() : 'U'}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '16px',
                            color: '#000000',
                            fontWeight: 600,
                            fontStyle: 'SemiBold',
                            letterSpacing: '0.4px',
                            lineHeight: '166%',
                          }}
                        >
                          {assignedTo || 'Unassigned'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {activeTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Stack Trace Error Message */}
                  <Box
                    sx={{
                      position: 'relative',
                      p: 2,
                      backgroundColor: '#D32F2F1F',
                      borderRadius: '8px',
                      border: '1px solid #FFE0E0',
                    }}
                  >
                    <Tooltip
                      title="Copied!"
                      open={copiedStackTrace}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      placement="top"
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(stackTraceMessage)
                          setCopiedStackTrace(true)
                          setTimeout(() => setCopiedStackTrace(false), 2000)
                        }}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          padding: '4px',
                          color: '#00000099',
                          '&:hover': {
                            backgroundColor: '#FFE0E0',
                          },
                        }}
                      >
                        <ContentCopy sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      component="pre"
                      sx={{
                        color: '#00000099',
                        fontWeight: 700,
                        fontStyle: 'Bold',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        leadingTrim: 'NONE',
                        lineHeight: '1.6',
                        letterSpacing: '0.17px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        margin: 0,
                      }}
                    >
                      {stackTraceMessage}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}
